import getCoverageQuestionsApi from 'api/coverageQuestion';
import { push } from 'connected-react-router';
import { call, put, select } from 'redux-saga/effects';
import appActions from 'redux/actions/applicationSpinnerActions';
import mobilityContinuationActions from 'redux/actions/mobilityContinuationActions';
import { caseGuidCreatedSelector } from 'redux/selectors/caseCreation';
import { partnerSelector, selectTenantId } from 'redux/selectors/config';
import { answersSelector } from 'redux/selectors/mobilityContinuation';
import { routes } from 'Routes/routesConfig';
import type {
  Answer,
  GetCoverageQuestionsApiBody,
  CoverageQuestionsState,
} from 'ts/types/coverage-questions.types';
import { getSynergySessionToken } from 'utils/sagas-utils';
import type { RemoveLastAnswerReturn } from './types';
import {
  getCoveredServices,
  getNotCoveredServices,
  isAnswerRemovable,
  removeExtraDataFromAnswers,
  removeLastAnswerFromCoverage,
} from './utils';

/**
 * 1. Check if we have all rejected services, or at least one
 * covered
 *
 * 2. Go to DropOff if there are no question for user
 *
 * 3. Set new question for user, and continue the flow
 */
export function* getCoverageQuestions() {
  try {
    yield put(appActions.startLoad({}));
    const tenantId: string = yield select(selectTenantId);
    const caseGuid: string = yield select(caseGuidCreatedSelector);
    const token: string = yield getSynergySessionToken();
    const answers: Answer[] = yield select(answersSelector);
    const partner: string = yield select(partnerSelector);

    const answersParsed = removeExtraDataFromAnswers(answers);

    const body: GetCoverageQuestionsApiBody = {
      case_guid: caseGuid,
      state: answersParsed || [],
    };

    const { result, status, ok } = yield call(getCoverageQuestionsApi, {
      tenantId,
      body,
      token,
      partner,
    });

    if (!ok || status !== 200) {
      throw new Error(result);
    }

    // 1
    const servicesCovered = getCoveredServices(result.coverageResponses);
    const servicesNotCovered = getNotCoveredServices(result.coverageResponses);
    const areAllServicesRejected =
      servicesNotCovered.length === result.coverageResponses.length;

    if (areAllServicesRejected) {
      throw new Error('No services to cover');
    }

    if (servicesCovered.length > 0) {
      yield put(
        mobilityContinuationActions.setCoverageQuestionsData({
          answers,
          data: result,
          coverage: servicesCovered,
        }),
      );
      yield put(push(routes.MOBILITY_CONTINUATION_SERVICES));
      return;
    }

    // 2
    if (!result.nextQuestion) {
      yield put(push(routes.MOBILITY_CONTINUATION_SERVICES));
      return;
    }

    // 3
    const wasFirstQuestionAsked = isAnswerRemovable(result.coverageResponses);
    yield put(
      mobilityContinuationActions.setCoverageQuestionsData({
        answers,
        data: result,
        wasAnswerRemoved: wasFirstQuestionAsked,
      }),
    );
  } catch (error) {
    console.error('Error while fetching coverage questions', error);
    yield put(push(routes.DROPOFF));
  } finally {
    yield put(appActions.stopLoad({}));
  }
}

export function* setCoverageAnswer(action: {
  type: string;
  payload: CoverageQuestionsState['answers'];
}) {
  try {
    const data = action.payload;
    yield put(mobilityContinuationActions.setCoverageResponse(data));
    yield put(mobilityContinuationActions.getCoverageQuestions());
  } catch (error) {
    console.error("Error while setting user's coverage answer", error);
    yield put(push(routes.DROPOFF));
  }
}

export function* goToPreviousQuestion() {
  try {
    const answers: Answer[] = yield select(answersSelector);
    const data: RemoveLastAnswerReturn = yield removeLastAnswerFromCoverage(
      answers,
    );

    if (!data.wasAnswerRemoved) {
      yield put(push(routes.MOBILITY_CONTINUITY_LOCATION));
      return;
    }
    yield put(mobilityContinuationActions.setCoverageQuestionsData(data));
    yield put(mobilityContinuationActions.getCoverageQuestions());
  } catch (error) {
    console.log('Error while removing last answered question', error);
    yield put(push(routes.DROPOFF));
  }
}
